import { FC, memo, useCallback, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';

import { TippyDefault } from 'app/components/Tippy';
import Typography from 'app/components/Typography';

import { updateAlertSuppression, updateAlertSuppression25 } from 'app/modules/alerts/core/request';
import { useAlertStore } from 'app/modules/alerts/store';

import { useBoolean, useNumber } from 'app/hooks';

import { AlertItem } from './AlertItem';
import { Alert, AlertType, SuppressionResponse, TAB_ALERTS } from './core/type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

interface Props {
  type: AlertType;
  index: number;
  alerts: Array<Alert>;
  isShowImg?: boolean;
  tab: TAB_ALERTS;
}

export const AlertsGroup: FC<Props> = memo(({ type, index, alerts, isShowImg = false, tab }) => {
  const alertId = useNumber();
  const alertId25 = useNumber();

  const isOpen = useBoolean(true);

  const queryClient = useQueryClient();

  const alertSuppressionItems = useAlertStore((s) => s.state.alertSuppressionItems);
  const alertSuppression25Items = useAlertStore((s) => s.state.alertSuppression25Items);

  const organizationId = useOrganizationStore((s) => s.state.organization?.id);

  const { mutate: updateAlertSuppressionMutation, isLoading: isLoadingUpdateAlertSuppression } =
    useMutation<void, Error, { relation: string | number; active: boolean }[]>({
      mutationKey: ['update-alert-suppression', type],
      mutationFn: (payload: { relation: string | number; active: boolean }[]) =>
        updateAlertSuppression(type, payload),
      onSuccess: () => {
        queryClient.invalidateQueries(['alertSuppression', type, organizationId]);
      },
    });

  // SIMS 2.5's own suppression — independent mutation, independent table on the backend.
  const {
    mutate: updateAlertSuppression25Mutation,
    isLoading: isLoadingUpdateAlertSuppression25,
  } = useMutation<void, Error, { relation: string | number; active: boolean }[]>({
    mutationKey: ['update-alert-suppression-2-5', type],
    mutationFn: (payload: { relation: string | number; active: boolean }[]) =>
      updateAlertSuppression25(type, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['alertSuppression25', type, organizationId]);
    },
  });

  const extractAlertTypeId = (description: string): string | null => {
    const match = description.match(/<b>(.*?)<\/b>/);
    return match ? match[1] : null;
  };

  // Shared between the SIMS 2.0 and 2.5 suppression maps — same matching logic
  // (including gateway/hanger-sensor propagation), different data source.
  const computeSuppressed = useCallback(
    (
      suppressionKey: string | null | undefined,
      map: Map<string, boolean>,
      items: SuppressionResponse[],
      description: string,
    ): boolean => {
      let result = suppressionKey ? map.get(suppressionKey) === true : false;

      if (!result && suppressionKey && tab === TAB_ALERTS.DEVICE_CONNECTION) {
        const includingMatch = description.match(/Including\s+(.+)$/);

        if (includingMatch) {
          const hangerSensorNames = includingMatch[1].split(',').map((name) => name.trim());

          result = hangerSensorNames.some((sensorName) => {
            const suppressionItem = items.find((item) => item.name === sensorName);
            return suppressionItem?.active === true;
          });
        }
      }

      return result;
    },
    [tab],
  );

  const suppressionMap = useMemo(() => {
    const map = new Map<string, boolean>();
    alertSuppressionItems?.forEach((item) => {
      const key = tab === TAB_ALERTS.DEVICE_CONNECTION ? item.name : item.sku;
      map.set(key, item.active);
    });
    return map;
  }, [alertSuppressionItems, tab]);

  const suppressionMap25 = useMemo(() => {
    const map = new Map<string, boolean>();
    alertSuppression25Items?.forEach((item) => {
      const key = tab === TAB_ALERTS.DEVICE_CONNECTION ? item.name : item.sku;
      map.set(key, item.active);
    });
    return map;
  }, [alertSuppression25Items, tab]);

  // Same key derivation computeSuppressed/suppressionMap use to display the badge
  // (bolded device name from the description, or SKU outside the device-connection
  // tab) - matching on this instead of a raw description substring check means a
  // click actually finds the device the badge says it should.
  const getSuppressionItemKey = useCallback(
    (item: SuppressionResponse) => (tab === TAB_ALERTS.DEVICE_CONNECTION ? item.name : item.sku),
    [tab],
  );

  const handleToggleSuppression = useCallback(
    (alert: Alert, suppressionKey: string | null | undefined) => async () => {
      const matchedSuppression = alertSuppressionItems.find(
        (item) => suppressionKey != null && getSuppressionItemKey(item) === suppressionKey,
      );

      if (!matchedSuppression) return;

      const payload = {
        relation: matchedSuppression.id,
        active: !matchedSuppression.active,
      };

      alertId.setValue(alert.id);
      updateAlertSuppressionMutation([payload]);
    },
    [alertSuppressionItems, getSuppressionItemKey, updateAlertSuppressionMutation],
  );

  const handleToggleSuppression25 = useCallback(
    (alert: Alert, suppressionKey: string | null | undefined) => async () => {
      const matchedSuppression = alertSuppression25Items.find(
        (item) => suppressionKey != null && getSuppressionItemKey(item) === suppressionKey,
      );

      if (!matchedSuppression) return;

      const payload = {
        relation: matchedSuppression.id,
        active: !matchedSuppression.active,
      };

      alertId25.setValue(alert.id);
      updateAlertSuppression25Mutation([payload]);
    },
    [alertSuppression25Items, getSuppressionItemKey, updateAlertSuppression25Mutation],
  );

  return (
    <div style={{ marginTop: index === 0 ? 0 : 16 }}>
      <div className={`accordion-item ${isOpen.value ? 'show' : ''}`}>
        <div className="d-flex align-items-center justify-content-between">
          <div
            className="accordion-header"
            id={`kt_accordion_1_header_${index}`}
            style={{
              width: 300,
              minWidth: 300,
              left: 0,
              zIndex: 1,
              cursor: 'default',
            }}
          >
            {/* <ButtonCollapsed
              className={`accordion-button collapsed ${isOpen.value ? 'show' : 0}`}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#kt_accordion_1_body_${index}`}
              aria-expanded={index === 0 ? 'true' : 'false'}
              aria-controls={`kt_accordion_1_body_${index}`}
              style={{ width: 'auto' }}
              onClick={() => {
                toggleShow();
              }}
            >
              <IconArrow
                style={{
                  transform: `rotate(${isOpen.value ? 180 : 0}deg)`,
                }}
              />
              <Typography
                fontSize={18}
                fontWeight={600}
                lineHeight={32}
                color="neutral-10"
                style={{
                  whiteSpace: 'nowrap',
                  marginLeft: 6,
                }}
              >
                {alertTypeText[type as AlertKey] ?? type} ({alerts.length})
              </Typography>
            </ButtonCollapsed> */}
          </div>
        </div>
        {/* <div
          id={`kt_accordion_1_body_${index}`}
          className={`accordion-collapse collapse ${isOpen.value ? 'show' : ''}`}
          aria-labelledby={`kt_accordion_1_header_${index}`}
          data-bs-parent="#kt_accordion_1"
          style={{
            marginTop: isOpen.value ? 12 : 0,
            height: isOpen.value ? 'auto' : 0,
            marginBottom: isOpen.value ? 56 : 0,
          }}
        >
          {alerts.map((alert) => (
            <div
              key={alert.id}
              style={{ marginBottom: 16 }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <AlertItem alert={alert} key={alert.id} isShowImg={isShowImg}></AlertItem>
            </div>
          ))}
        </div> */}

        {alerts.map((alert) => {
          const suppressionKey =
            tab === TAB_ALERTS.DEVICE_CONNECTION
              ? extractAlertTypeId(alert.content.description)
              : alert.content.itemSku;
          const description = alert.content?.description || '';

          const suppressed = computeSuppressed(
            suppressionKey,
            suppressionMap,
            alertSuppressionItems,
            description,
          );
          const suppressed25 = computeSuppressed(
            suppressionKey,
            suppressionMap25,
            alertSuppression25Items,
            description,
          );

          // A row whose device has no matching entry in *either* roster is
          // historically orphaned (renamed, deleted, or moved org) rather than
          // just failing to match one of the two systems' suppression lists.
          const matchedSuppression = alertSuppressionItems.find(
            (item) => suppressionKey != null && getSuppressionItemKey(item) === suppressionKey,
          );
          const matchedSuppression25 = alertSuppression25Items.find(
            (item) => suppressionKey != null && getSuppressionItemKey(item) === suppressionKey,
          );
          const isOrphaned =
            tab !== TAB_ALERTS.ROGUE_DEVICE && !matchedSuppression && !matchedSuppression25;

          return (
            <div
              key={alert.id}
              className="d-flex align-items-center gap-4 mb-4"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertItem alert={alert} isShowImg={isShowImg} isOrphaned={isOrphaned} />

              {/* Rogue devices have no registered inventory row to suppress against —
                  the intended resolution is to register the device, not silence the alert. */}
              {tab !== TAB_ALERTS.ROGUE_DEVICE && (
                <div className="d-flex align-items-center gap-2">
                  <TippyDefault
                    arrow={false}
                    placement="top"
                    content={!matchedSuppression ? 'Device no longer registered — resolve this alert instead' : ''}
                    disabled={!!matchedSuppression}
                  >
                    <span>
                      <Button
                        variant={!suppressed ? 'primary' : 'warning'}
                        onClick={handleToggleSuppression(alert, suppressionKey)}
                        className="d-flex align-items-center justify-content-center gap-2 mw-175px w-100"
                        disabled={isLoadingUpdateAlertSuppression || !matchedSuppression}
                      >
                        {alertId.value === alert.id && isLoadingUpdateAlertSuppression && (
                          <Spinner animation="border" size="sm" className="mb-1" />
                        )}
                        <Typography
                          fontSize={14}
                          fontWeight={500}
                          lineHeight={22}
                          color={!suppressed ? '#fff' : '#333'}
                        >
                          {!suppressed ? 'Mute on 2.0' : 'Muted on 2.0'}
                        </Typography>
                      </Button>
                    </span>
                  </TippyDefault>
                  <TippyDefault
                    arrow={false}
                    placement="top"
                    content={!matchedSuppression25 ? 'Device no longer registered — resolve this alert instead' : ''}
                    disabled={!!matchedSuppression25}
                  >
                    <span>
                      <Button
                        variant={!suppressed25 ? 'primary' : 'warning'}
                        onClick={handleToggleSuppression25(alert, suppressionKey)}
                        className="d-flex align-items-center justify-content-center gap-2 mw-175px w-100"
                        disabled={isLoadingUpdateAlertSuppression25 || !matchedSuppression25}
                      >
                        {alertId25.value === alert.id && isLoadingUpdateAlertSuppression25 && (
                          <Spinner animation="border" size="sm" className="mb-1" />
                        )}
                        <Typography
                          fontSize={14}
                          fontWeight={500}
                          lineHeight={22}
                          color={!suppressed25 ? '#fff' : '#333'}
                        >
                          {!suppressed25 ? 'Mute on 2.5' : 'Muted on 2.5'}
                        </Typography>
                      </Button>
                    </span>
                  </TippyDefault>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
